/**
 * One map from a quote's status to where it shows (S1c).
 *
 * The status set is the CHECK constraint's (chk_quotations_status_canonical, migration 314;
 * backend utils/quoteStatus.js CANONICAL_QUOTE_STATUSES): twelve canonical uppercase values.
 * Each lands in exactly one deliberate kanban column and one chip view; nothing falls into
 * Draft by default (it did: the kanban lower-cased the status and pushed anything it did
 * not recognise into Draft, which on prod read Draft 106 against the chip's 88).
 *
 * Chip views are S1a's (backend services/quoteListQuery.js): only DRAFT, SENT,
 * PENDING_APPROVAL, WON and LOST have a status view of their own; every other status is in
 * "all" only. The Pending chip is the server's pending_approval view, and so is the kanban's
 * Pending approval column — the two agree by construction.
 *
 * Column decisions, for the statuses with no view of their own:
 *   PENDING    -> Sent      a legacy open quote awaiting the customer (the POS lists
 *                           pending with sent and accepted as open, routes/pos-quotes.js)
 *   VIEWED     -> Viewed    the customer has opened a sent quote
 *   APPROVED   -> Approved  approved internally, not yet re-sent
 *   REJECTED   -> Rejected  refused internally; its only way on is back to Draft
 *   ACCEPTED   -> Won       the customer said yes
 *   CONVERTED  -> Won       the won-set is {WON, CONVERTED}
 *   EXPIRED    -> Lost      the offer lapsed
 * A status outside the set (none can be stored) goes to Other, which shows only when it
 * holds something.
 *
 * `tone` names the --status-<tone> token family that colours the column; `dropStatus` is the
 * canonical status a drop into the column asks for (null: not a drop target — the server
 * cannot set VIEWED, and Other is no status at all).
 */

export const QUOTE_STATUSES = [
  'DRAFT', 'SENT', 'VIEWED', 'PENDING', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED',
  'ACCEPTED', 'WON', 'LOST', 'CONVERTED', 'EXPIRED'
];

export const KANBAN_COLUMNS = [
  { id: 'draft', label: 'Draft', tone: 'draft', statuses: ['DRAFT'], dropStatus: 'DRAFT' },
  { id: 'sent', label: 'Sent', tone: 'sent', statuses: ['SENT', 'PENDING'], dropStatus: 'SENT' },
  { id: 'viewed', label: 'Viewed', tone: 'viewed', statuses: ['VIEWED'], dropStatus: null },
  { id: 'pending_approval', label: 'Pending approval', tone: 'pending', statuses: ['PENDING_APPROVAL'], dropStatus: 'PENDING_APPROVAL' },
  { id: 'approved', label: 'Approved', tone: 'approved', statuses: ['APPROVED'], dropStatus: 'APPROVED' },
  { id: 'rejected', label: 'Rejected', tone: 'rejected', statuses: ['REJECTED'], dropStatus: 'REJECTED' },
  { id: 'won', label: 'Won', tone: 'won', statuses: ['WON', 'ACCEPTED', 'CONVERTED'], dropStatus: 'WON' },
  { id: 'lost', label: 'Lost', tone: 'lost', statuses: ['LOST', 'EXPIRED'], dropStatus: 'LOST' }
];

/** For a status outside the canonical set: shown only when it holds something. */
export const OTHER_COLUMN = { id: 'other', label: 'Other', tone: 'expired', statuses: [], dropStatus: null };

const COLUMN_BY_STATUS = {};
for (const col of KANBAN_COLUMNS) for (const s of col.statuses) COLUMN_BY_STATUS[s] = col.id;

/** S1a's status views. Every other status is in "all" only. */
const CHIP_VIEW_BY_STATUS = {
  DRAFT: 'draft',
  SENT: 'sent',
  PENDING_APPROVAL: 'pending_approval',
  WON: 'won',
  LOST: 'lost'
};

const canonical = (status) => String(status || '').trim().toUpperCase().replace(/[\s-]+/g, '_');

/** The kanban column id a status belongs in ('other' if it is not a canonical status). */
export function kanbanColumnFor(status) {
  return COLUMN_BY_STATUS[canonical(status)] || OTHER_COLUMN.id;
}

/**
 * A status as it is shown: its column's { label, tone } (the column object; OTHER_COLUMN for a
 * status outside the set). The slot's quote rows and the customer pane's recent quotes read it.
 */
export function quoteStatusDisplay(status) {
  const id = kanbanColumnFor(status);
  return KANBAN_COLUMNS.find((c) => c.id === id) || OTHER_COLUMN;
}

/** The chip view (S1a view) a status is counted under besides "all". */
export function chipViewFor(status) {
  return CHIP_VIEW_BY_STATUS[canonical(status)] || 'all';
}
